import { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { ReactComponent as FileImportIC } from "../../assets/svg/file-import-icon.svg";
import { ReactComponent as ExcelIC } from "../../assets/svg/excel-icon.svg";
import { ReactComponent as TemplateDownloadIC } from "../../assets/svg/template-download-icon.svg";

export default function ImportFileModal({ open, onClose, onImport, onExport, type, isImporting }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);

  const fileInputRef = useRef(null);

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setFileBase64(null);

      // Clear file input also
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);  // runs whenever "open" changes

  if (!open) return null;


  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];

    if (file) {
      setSelectedFile(file);

      const base64 = await convertToBase64(file);
      setFileBase64(base64);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];

    if (file) {
      setSelectedFile(file);

      const base64 = await convertToBase64(file);
      setFileBase64(base64);

      console.log("Selected File:", file);
      console.log("Selected Base64:", base64);
    } else {
      alert("Please select a valid file.");
    }
  };

  const removeFile = () => setSelectedFile(null);




  return (
    <>
      {/* ✅ INTERNAL CSS */}
      <style>
        {`
        .import-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          z-index: 999;
          display: flex;
          justify-content: center;
          align-items: center;
          pointer-events: ${open ? "auto" : "none"};
          opacity: ${open ? "1" : "0"};
          transition: opacity 0.3s ease;
        }

         .import-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .close-btn {
          cursor: pointer;
        }

        .import-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.4);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: import-spin 0.7s linear infinite;
        }

        @keyframes import-spin {
          to { transform: rotate(360deg); }
        }

        `}
      </style>

      {ReactDOM.createPortal(
      <div className="import-overlay fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-xl w-[420px] p-6 relative" style={{ borderRadius: '24px', padding: '24px' }}>


          <div className="import-header">
            <div style={{ fontWeight: "700", fontSize: "20px", color: "#212129", margin: 0 }}>
              Import file
              <div style={{ fontSize: '14px', fontWeight: '400', color: '#8790A1' }}>
                Pull your {type || "leads"} from the uploaded file.
              </div>
            </div>

            <span className="close-btn" onClick={onClose}>✖</span>
          </div>


          {/* Upload Box */}
          {!selectedFile ? (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={openFileDialog}

              style={{ border: '1px dashed #8790A1', borderRadius: '16px', marginTop: '16px', textAlign: 'center', padding: '24px 16px' }}
            >
              <div className="cursor-pointer flex flex-col items-center space-y-2">
                <FileImportIC />
                <div style={{ fontSize: '14px', fontWeight: '400', color: '#8790A1' }}>Drag and drop a file</div>
                <div style={{ fontSize: '14px', fontWeight: '400', color: '#8790A1' }}>or</div>
                <div style={{ color: '#3A53C2', fontSize: '14px', fontWeight: '500' }}>Select a file</div>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ visibility: 'hidden' }}
                  onChange={handleFileChange}
                />
              </div>
            </div>
          ) : (
            <div
              style={{
                backgroundColor: '#F8F8F8',
                padding: '12px 16px',
                borderRadius: '12px',
                width: '492px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <ExcelIC />

              <div style={{ flexGrow: 1, marginLeft: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#172B4D' }}>
                  {selectedFile.name}
                </div>
                <div style={{ fontSize: '12px', fontWeight: '400', color: '#8790A1' }}>
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </div>
              </div>

              <div
                onClick={removeFile}
                style={{
                  color: '#8790A1',
                  borderRadius: '100px',
                  backgroundColor: 'white',
                  height: '28px',
                  width: '28px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                x
              </div>
            </div>

          )}

          {/* Footer buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",  // ✅ pushes one left, one right
              alignItems: "center",
              marginTop: "16px",
              width: "100%", // optional but recommended
            }}
          >
            {/* LEFT BUTTON */}

            <div
              onClick={onExport}
              style={{
                border: "1px solid #3D4AF5",
                borderRadius: "45px",
                padding: "8px 24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <TemplateDownloadIC />
              <span style={{ fontWeight: '500', fontSize: '14px', color: '#3D4AF5' }}>Template</span>
            </div>

            {/* RIGHT BUTTON */}
            <div
              onClick={() => {
                if (selectedFile && !isImporting) {
                  onImport(fileBase64);
                }
              }}
              style={{
                borderRadius: "45px",
                padding: "8px 32px",
                backgroundColor: selectedFile ? "#3D4AF5" : "#A0A8C0",
                color: "white",
                fontWeight: "500",
                fontSize: "14px",
                cursor: selectedFile && !isImporting ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                minWidth: "88px",
              }}
            >
              {isImporting ? (
                <>
                  <span className="import-spinner" />
                  Importing...
                </>
              ) : (
                "Import"
              )}
            </div>
          </div>

        </div>
      </div>, document.body)}
    </>
  );
}